'use client'

import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import useSWR from 'swr'

import {
  ChevronRight,
  LogOut,
  Settings,
  ShoppingBag,
  ReceiptText,
  User,
  Zap,
  Sun,
  Moon,
} from 'lucide-react'

import {
  useSellerId,
} from '@/hooks/useSellerId'

import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { getImageCandidates } from '@/lib/image'
import { API_CONFIG } from '@/config/api'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

type UserProfile = {
  customer_id: number
  firstname: string
  lastname: string
  email: string
  username: string
  shortprofile?: string | null
  avatar?: string | null
  ai_credits?: number | null
}

function initials(firstname?: string, lastname?: string) {
  const a = (firstname?.[0] ?? '').toUpperCase()
  const b = (lastname?.[0] ?? '').toUpperCase()
  return (a + b) || 'U'
}

export function HeaderUserDropdown({
  className,
  showName = false,
}: {
  className?: string
  showName?: boolean
}) {
  const router = useRouter()
  const { theme, toggleTheme } = useTheme()
  const { user, userId, isLoading: userLoading } = useCurrentUser()
  const { sellerId } = useSellerId()

  const {
    data: progressResponse,
    isLoading: energyLoading,
  } = useSWR(
    sellerId
      ? `/api/moments/${sellerId}/progress`
      : null,

    async (
      url: string
    ) => {
      const response =
        await fetch(
          url,
          {
            credentials:
              'include',

            cache:
              'no-store',
          }
        )

      const json =
        await response
          .json()
          .catch(
            () => null
          )

      if (!response.ok) {
        throw new Error(
          json?.error ||
            'ENERGY_LOAD_FAILED'
        )
      }

      return json
    },

    {
      revalidateOnFocus:
        true,

      revalidateOnReconnect:
        true,

      refreshInterval:
        10000,
    }
  )

  const progress =
    progressResponse
      ?.progress ??
    progressResponse
      ?.data ??
    progressResponse

  const energy =
    progress?.energy

  const energyPercent =
    Math.min(
      100,
      Math.max(
        0,
        Number(
          energy?.percent ||
            0
        )
      )
    )

  const lowEnergy =
    energyPercent < 10

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [liveAvatar, setLiveAvatar] = useState(user?.avatar || user?.avatar_url || '')

  useEffect(() => {
    function handleAvatarUpdated(event: Event) {
      const customEvent = event as CustomEvent<{ avatar?: string }>
      const nextAvatar = customEvent.detail?.avatar

      if (nextAvatar) {
        setLiveAvatar(nextAvatar)
      }
    }

    window.addEventListener('profile-avatar-updated', handleAvatarUpdated)

    return () => {
      window.removeEventListener('profile-avatar-updated', handleAvatarUpdated)
    }
  }, [])

  useEffect(() => {
    if (!userId) return
    let alive = true

    setLoading(true)

    fetch(`/api/user-profile/${userId}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive) return
        const nextProfile = data && !data.error ? data : null
        setProfile(nextProfile)

      })
      .catch(() => {
        if (!alive) return
        setProfile(null)
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [userId])

  const showLoading = userLoading || loading

  const avatarUrl = user?.avatar?.trim?.() || null
  const firstname = user?.firstname || ''
  const lastname = user?.lastname || ''
  const fullName = `${firstname} ${lastname}`.trim() || 'Mi cuenta'
  const email = profile?.email || user?.email || ''
  const username = profile?.username || user?.username || ''

  const onLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' })
    } catch {}

    router.push('/login')
    router.refresh()
  }

  const avatarCandidates = useMemo(
    () =>
      getImageCandidates(avatarUrl, {
        width: 42,
        height: 42,
        siteOrigin: API_CONFIG.BASE_URL,
        assetBaseUrl: process.env.NEXT_PUBLIC_ASSET_BASE_URL,
      }),
    [avatarUrl]
  )

  //console.log('avatarCandidates: ', avatarCandidates)

  const [avatarIndex, setAvatarIndex] = useState(0)

  useEffect(() => {
    setAvatarIndex(0)
  }, [avatarUrl])

  const resolvedAvatarSrc = avatarCandidates[avatarIndex] || ''

  //console.log('resolvedAvatar: ', resolvedAvatarSrc)

  return (
    <div className={cn('shrink-0 inline-flex', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'hubee-header-control !h-12 !w-12 !rounded-[16px] !p-1',
              showName && '!w-auto gap-2 !px-2'
            )}
            aria-label="Abrir menú de usuario"
          >
            {showLoading ? (
              <>
                <Skeleton className="h-9 w-9 rounded-[12px]" />
                {showName && (
                  <div className="hidden min-w-0 sm:block">
                    <Skeleton className="h-3 w-24" />
                  </div>
                )}
              </>
            ) : (
              <>                
                <Avatar className="h-9 w-9 shrink-0 !rounded-[12px]">
                  {avatarUrl ? (
                    <AvatarImage
                      src={liveAvatar || resolvedAvatarSrc}
                      alt={fullName}
                      className="!rounded-[12px] object-cover"
                      onError={() => {
                        setAvatarIndex((prev) =>
                          prev < avatarCandidates.length - 1
                            ? prev + 1
                            : prev
                        )
                      }}
                    />
                  ) : (
                    <AvatarFallback className="!rounded-[12px]">
                      {initials(firstname, lastname)}
                    </AvatarFallback>
                  )}
                </Avatar>

                {showName && (
                  <div className="hidden min-w-0 sm:block">
                    <div className="max-w-[140px] truncate text-sm font-medium text-foreground">
                      {fullName}
                    </div>
                  </div>
                )}
              </>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          side="bottom"
          sideOffset={10}
          className="
            z-[220]
            w-[300px]
            rounded-2xl
            border-border/60
            p-2
            shadow-2xl
          "
        >
          {/* PROFILE */}

          <button
            type="button"
            onClick={() => {
              if (!username) {
                return
              }

              router.push(
                `/profile/${encodeURIComponent(
                  username
                )}`
              )
            }}
            className="
              group
              flex
              w-full
              items-center
              gap-3
              rounded-xl
              px-2.5
              py-2.5
              text-left
              transition-colors
              hover:bg-muted/60
            "
          >
            <Avatar className="h-11 w-11 shrink-0">
              {avatarUrl ? (
                <AvatarImage
                  src={
                    liveAvatar ||
                    resolvedAvatarSrc
                  }
                  alt={
                    fullName
                  }
                  onError={() => {
                    setAvatarIndex(
                      prev =>
                        prev <
                        avatarCandidates.length -
                          1
                          ? prev + 1
                          : prev
                    )
                  }}
                />
              ) : (
                <AvatarFallback>
                  {initials(
                    firstname,
                    lastname
                  )}
                </AvatarFallback>
              )}
            </Avatar>

            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-semibold text-foreground">
                {fullName}
              </div>

              <div className="mt-0.5 flex items-center gap-1 text-[12px] text-muted-foreground">
                <span>
                  Ver perfil
                </span>

                <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </button>

          <DropdownMenuSeparator className="my-2" />

          {/* ENERGY */}

          <DropdownMenuItem
            onSelect={() => {
              window.dispatchEvent(
                new CustomEvent(
                  'hubee:buy_credits_open'
                )
              )
            }}
            className="
              group
              flex
              cursor-pointer
              items-center
              gap-3
              rounded-xl
              px-2.5
              py-2.5
              focus:bg-muted/60
            "
          >
            <div
              className="
                relative
                flex
                h-11
                w-11
                shrink-0
                items-center
                justify-center
                rounded-full
              "
            >
              <svg
                viewBox="0 0 44 44"
                className="
                  absolute
                  inset-0
                  h-full
                  w-full
                  -rotate-90
                "
              >
                <circle
                  cx="22"
                  cy="22"
                  r="18"
                  fill="none"
                  stroke="hsl(var(--muted))"
                  strokeWidth="3"
                />

                <circle
                  cx="22"
                  cy="22"
                  r="18"
                  fill="none"
                  stroke={
                    lowEnergy
                      ? '#f87171'
                      : 'hsl(var(--primary))'
                  }
                  strokeWidth="3"
                  strokeLinecap="round"
                  pathLength="100"
                  strokeDasharray={
                    `${energyPercent} 100`
                  }
                  className="
                    transition-all
                    duration-700
                  "
                />
              </svg>

              <Zap
                className={cn(
                  'relative h-[17px] w-[17px]',
                  lowEnergy
                    ? 'text-red-500'
                    : 'text-primary'
                )}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-3
                "
              >
                <span
                  className="
                    text-[14px]
                    font-medium
                    text-foreground
                  "
                >
                  Energía
                </span>

                {energyLoading ? (
                  <Skeleton
                    className="h-4 w-10"
                  />
                ) : (
                  <span
                    className={cn(
                      'text-[13px] font-semibold tabular-nums',
                      lowEnergy
                        ? 'text-red-500'
                        : 'text-primary'
                    )}
                  >
                    {energyPercent.toFixed(
                      energyPercent % 1 ===
                        0
                        ? 0
                        : 1
                    )}
                    %
                  </span>
                )}
              </div>

              <div
                className="
                  mt-0.5
                  flex
                  items-center
                  gap-1
                  text-[12px]
                  text-muted-foreground
                "
              >
                <span>
                  Recargar energía
                </span>

                <ChevronRight
                  className="
                    h-3
                    w-3
                    transition-transform
                    group-hover:translate-x-0.5
                  "
                />
              </div>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-2" />

          {/* COMPRAS */}
          <DropdownMenuItem
            onSelect={() => {
              router.push(
                '/settings/orders'
              )
            }}
            className="
              cursor-pointer
              rounded-xl
              px-3
              py-2.5
              text-[14px]
            "
          >
            <ShoppingBag className="mr-3 h-[18px] w-[18px] text-muted-foreground" />

            Compras
          </DropdownMenuItem>

          {/* SETTINGS */}
          <DropdownMenuItem
            onSelect={() => {
              router.push(
                '/settings/profile'
              )
            }}
            className="
              cursor-pointer
              rounded-xl
              px-3
              py-2.5
              text-[14px]
            "
          >
            <Settings className="mr-3 h-[18px] w-[18px] text-muted-foreground" />

            Configuración
          </DropdownMenuItem><DropdownMenuSeparator className="my-2" />

          {/* LOGOUT */}

          <DropdownMenuItem
            onClick={
              onLogout
            }
            className="
              cursor-pointer
              rounded-xl
              px-3
              py-2.5
              text-[14px]
              text-red-600
              focus:bg-red-500/10
              focus:text-red-600
              dark:text-red-400
              dark:focus:text-red-400
            "
          >
            <LogOut className="mr-3 h-[18px] w-[18px]" />

            Cerrar sesión
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-2" />

          {/* THEME — iconos sin etiquetas */}
          <div className="px-2 py-1.5" role="group" aria-label="Apariencia">
            <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/50 p-1">
              <button
                type="button"
                aria-label="Modo claro"
                title="Modo claro"
                aria-pressed={theme === 'light'}
                onClick={() => { if (theme !== 'light') toggleTheme() }}
                className={cn(
                  'flex h-9 flex-1 items-center justify-center rounded-lg transition-colors',
                  theme === 'light'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Sun className="h-[18px] w-[18px]" />
              </button>
              <button
                type="button"
                aria-label="Modo oscuro"
                title="Modo oscuro"
                aria-pressed={theme === 'dark'}
                onClick={() => { if (theme !== 'dark') toggleTheme() }}
                className={cn(
                  'flex h-9 flex-1 items-center justify-center rounded-lg transition-colors',
                  theme === 'dark'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Moon className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}